import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Plus, Coin } from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { listUcc, createUcc, listCooperators } from '../../services/cmcApi';
import { CmcPageHeader, CmcStatusBadge, CmcEmpty, fmtAmount, fmtDateOnly } from '../../components/cmc/CmcCommon';
import { Checkbox } from '../../components/ui/checkbox';
import { useBatchSelection } from '../../hooks/useBatchSelection';
import { batchSetUccStatus } from '../../services/api';

const emptyForm = {
  cooperator_id: '',
  label: '',
  family: 'GOUVERNANCE',
  ucc_quantity: 1,
  unit_value: 100,
  confidentiality_level: 'standard',
};

const FAMILIES = [
  { value: 'GOUVERNANCE', label: 'Gouvernance' },
  { value: 'DEVELOPPEMENT', label: 'Développement' },
  { value: 'EXPERTISE', label: 'Expertise' },
  { value: 'PREVENTION_SANTE_NON_ASSURANTIELLE', label: 'Prévention santé (non assurantielle)' },
  { value: 'PREVENTION_MOBILITE_VIEILLESSE', label: 'Prévention mobilité / vieillesse' },
  { value: 'ACCOMPAGNEMENT_VIE', label: 'Accompagnement de vie' },
  { value: 'SOUTIEN_FAMILIAL', label: 'Soutien familial' },
  { value: 'SOUTIEN_SOCIAL', label: 'Soutien social' },
  { value: 'SOUTIEN_EXCEPTIONNEL', label: 'Soutien exceptionnel' },
  { value: 'FRAIS_CONTRIBUTEUR', label: 'Frais contributeur' },
  { value: 'FORMATION_PARCOURS_COOPERATIF', label: 'Formation / parcours coopératif' },
  { value: 'ENVIRONNEMENT_TRANSITION', label: 'Environnement / transition' },
  { value: 'AUTRE', label: 'Autre' },
];

const CONFIDENTIALITY = [
  { value: 'standard', label: 'Standard' },
  { value: 'sensitive', label: 'Sensible' },
];

export default function CmcUccPage() {
  const [items, setItems] = useState([]);
  const [cooperators, setCooperators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const batch = useBatchSelection();
  const [batchBusy, setBatchBusy] = useState(false);

  const doBatchStatus = async (newStatus) => {
    if (!batch.hasSelection) return;
    setBatchBusy(true);
    try {
      const r = await batchSetUccStatus(Array.from(batch.selectedIds), newStatus);
      toast.success(`${r.data.updated.length} UCC → ${newStatus}, ${r.data.skipped.length} ignorée(s)`);
      batch.clear();
      fetchData();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur batch'); }
    setBatchBusy(false);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [u, c] = await Promise.all([listUcc({ limit: 200 }), listCooperators({ limit: 200, status: 'active' })]);
      setItems(u.data.items || []);
      setCooperators(c.data.items || []);
    } catch {
      toast.error('Erreur de chargement des UCC');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const submit = async () => {
    if (!form.cooperator_id) { toast.error('Sélectionnez un coopérateur'); return; }
    if (!form.label) { toast.error('Le libellé est requis'); return; }
    setSaving(true);
    try {
      await createUcc({
        ...form,
        ucc_quantity: parseFloat(form.ucc_quantity) || 0,
        unit_value: parseFloat(form.unit_value) || 0,
      });
      toast.success('UCC créée');
      setDialogOpen(false);
      setForm(emptyForm);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail?.[0]?.msg || 'Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  const coopMap = Object.fromEntries(cooperators.map((c) => [c.id, c]));
  const coopName = (id) => {
    const c = coopMap[id];
    if (!c) return id;
    return c.type === 'personne_morale' ? c.legal_name : `${c.first_name || ''} ${c.last_name || ''}`.trim();
  };

  return (
    <div data-testid="cmc-ucc-page">
      <CmcPageHeader
        title="Unités de Contribution Coopérative (UCC)"
        subtitle="Comptabilisation des contributions reçues par chaque coopérateur"
        actions={
          <Button onClick={() => { setForm(emptyForm); setDialogOpen(true); }} className="bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid="ucc-create-btn">
            <Plus size={16} className="mr-2" /> Nouvelle UCC
          </Button>
        }
      />

      {batch.hasSelection && (
        <Card className="border-[#2D5A4C] bg-[#F0F8E4] mb-4" data-testid="ucc-batch-toolbar">
          <CardContent className="p-3 flex items-center gap-3 flex-wrap">
            <p className="text-sm font-bold text-[#2D5A4C]">{batch.count} sélectionnée(s)</p>
            <div className="flex-1"></div>
            <Button size="sm" onClick={() => doBatchStatus('active')} disabled={batchBusy} className="bg-[#80C51F] hover:bg-[#6ba317] text-white" data-testid="ucc-batch-activate">
              {batchBusy && <Loader2 size={12} className="animate-spin mr-1" />} Activer
            </Button>
            <Button size="sm" onClick={() => doBatchStatus('inactive')} disabled={batchBusy} className="bg-[#5E6D66] hover:bg-[#4a5750] text-white" data-testid="ucc-batch-deactivate">
              Désactiver
            </Button>
            <Button size="sm" variant="ghost" onClick={batch.clear} data-testid="ucc-batch-clear">Effacer</Button>
          </CardContent>
        </Card>
      )}

      <Card className="border-[#E8E6E1]">
        <CardContent className="p-0">
          {loading ? (
            <div className="py-16 flex justify-center"><Loader2 className="h-7 w-7 animate-spin text-[#2D5A4C]" /></div>
          ) : items.length === 0 ? (
            <CmcEmpty icon={Coin} message="Aucune UCC enregistrée" hint="Créez la première UCC d'un coopérateur" />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F9F8F5] text-[11px] uppercase tracking-wide text-[#5E6D66]">
                  <tr>
                    <th className="text-left px-3 py-2.5 font-medium w-8">
                      <Checkbox
                        checked={items.length > 0 && items.every((u) => batch.isSelected(u.id))}
                        onCheckedChange={(v) => (v ? batch.selectAll(items) : batch.clear())}
                        data-testid="ucc-batch-select-all"
                      />
                    </th>
                    <th className="text-left px-4 py-2.5 font-medium">Libellé</th>
                    <th className="text-left px-4 py-2.5 font-medium">Coopérateur</th>
                    <th className="text-left px-4 py-2.5 font-medium">Famille</th>
                    <th className="text-right px-4 py-2.5 font-medium">Quantité</th>
                    <th className="text-right px-4 py-2.5 font-medium">Valeur unitaire</th>
                    <th className="text-right px-4 py-2.5 font-medium">Total</th>
                    <th className="text-left px-4 py-2.5 font-medium">Confidentialité</th>
                    <th className="text-left px-4 py-2.5 font-medium">Statut</th>
                    <th className="text-left px-4 py-2.5 font-medium">Créé</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F0EFEB]">
                  {items.map((u) => (
                    <tr key={u.id} className={`hover:bg-[#FAFAF8] ${batch.isSelected(u.id) ? 'bg-[#F0F8E4]' : ''}`} data-testid={`ucc-row-${u.id}`}>
                      <td className="px-3 py-3">
                        <Checkbox
                          checked={batch.isSelected(u.id)}
                          onCheckedChange={() => batch.toggle(u.id)}
                          data-testid={`ucc-select-${u.id}`}
                        />
                      </td>
                      <td className="px-4 py-3 text-[#1A2421] font-medium">{u.label}</td>
                      <td className="px-4 py-3 text-xs text-[#5E6D66]">{coopName(u.cooperator_id)}</td>
                      <td className="px-4 py-3 text-xs text-[#5E6D66]">
                        {(FAMILIES.find((f) => f.value === u.family)?.label) || u.family}
                      </td>
                      <td className="px-4 py-3 text-right text-[#1A2421]">{u.ucc_quantity}</td>
                      <td className="px-4 py-3 text-right text-[#1A2421]">{fmtAmount(u.unit_value)}</td>
                      <td className="px-4 py-3 text-right font-semibold text-[#1A2421]">{fmtAmount(u.total_value)}</td>
                      <td className="px-4 py-3 text-xs">
                        {u.confidentiality_level === 'sensitive' ? (
                          <span className="text-amber-700 font-medium">Sensible</span>
                        ) : (
                          <span className="text-[#5E6D66]">Standard</span>
                        )}
                      </td>
                      <td className="px-4 py-3"><CmcStatusBadge status={u.status} /></td>
                      <td className="px-4 py-3 text-xs text-[#5E6D66]">{fmtDateOnly(u.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-xl" data-testid="ucc-dialog">
          <DialogHeader>
            <DialogTitle>Nouvelle UCC</DialogTitle>
            <DialogDescription>Enregistrez une unité de contribution coopérative.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label>Coopérateur *</Label>
              <Select value={form.cooperator_id} onValueChange={(v) => setForm({ ...form, cooperator_id: v })}>
                <SelectTrigger data-testid="ucc-form-cooperator"><SelectValue placeholder="Choisir un coopérateur" /></SelectTrigger>
                <SelectContent>
                  {cooperators.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.type === 'personne_morale' ? c.legal_name : `${c.first_name || ''} ${c.last_name || ''}`.trim()} ({c.numero_associe})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label>Libellé *</Label>
              <Input data-testid="ucc-form-label" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} />
            </div>
            <div>
              <Label>Famille</Label>
              <Select value={form.family} onValueChange={(v) => setForm({ ...form, family: v })}>
                <SelectTrigger data-testid="ucc-form-family"><SelectValue /></SelectTrigger>
                <SelectContent>{FAMILIES.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Confidentialité</Label>
              <Select value={form.confidentiality_level} onValueChange={(v) => setForm({ ...form, confidentiality_level: v })}>
                <SelectTrigger data-testid="ucc-form-confidentiality"><SelectValue /></SelectTrigger>
                <SelectContent>{CONFIDENTIALITY.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Quantité</Label>
              <Input type="number" data-testid="ucc-form-quantity" value={form.ucc_quantity} onChange={(e) => setForm({ ...form, ucc_quantity: e.target.value })} />
            </div>
            <div>
              <Label>Valeur unitaire (€)</Label>
              <Input type="number" data-testid="ucc-form-unit-value" value={form.unit_value} onChange={(e) => setForm({ ...form, unit_value: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="ucc-cancel-btn">Annuler</Button>
            <Button onClick={submit} disabled={saving} className="bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid="ucc-save-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
